import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { Gauge, Sparkles, Loader2, AlertTriangle, ListChecks } from "lucide-react";
import { toast } from "sonner";

const TIER_COLORS = {
  low: "text-emerald-400",
  medium: "text-amber-500",
  high: "text-orange-400",
  critical: "text-red-400",
};

export default function RiskAnalysisPanel({ rfpId }) {
  const [risk, setRisk] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get(`/rfps/${rfpId}/risk-score`).then(({ data }) => setRisk(data)).catch(() => {});
  }, [rfpId]);

  const run = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/rfps/${rfpId}/risk-score`);
      setRisk(data);
      toast.success("Risk analysis complete");
    } catch { toast.error("Risk analysis failed"); }
    finally { setGenerating(false); }
  };

  const hasData = risk?.overall_score != null;
  const color = hasData ? TIER_COLORS[risk.tier] || "text-amber-500" : "text-text-secondary";

  return (
    <div className="tray p-6" data-testid="risk-panel">
      <div className="flex justify-between items-start mb-5 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Gauge className={`w-6 h-6 ${color}`} />
          <div>
            <div className="font-mono-data text-[10px] tracking-[0.3em] uppercase text-amber-500">AI Risk Score</div>
            <div className="font-display text-2xl mt-0.5">
              {hasData ? (
                <>
                  <span className={color}>{risk.overall_score}</span>
                  <span className="text-text-secondary text-sm font-mono-data ml-2">/ 100 · {risk.tier?.toUpperCase()}</span>
                </>
              ) : (
                <span className="text-text-secondary">Not yet analyzed</span>
              )}
            </div>
          </div>
        </div>
        <button onClick={run} disabled={generating} data-testid="run-risk-btn"
          className="btn-tactile inline-flex items-center gap-2 text-xs">
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-amber-500" />}
          {hasData ? "Re-analyze" : "Run AI Risk Analysis"}
        </button>
      </div>

      {hasData && (
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="font-mono-data text-[10px] tracking-widest uppercase text-text-secondary mb-2">Risk Factors</div>
            <div className="space-y-2">
              {(risk.factors || []).map((f, i) => (
                <div key={i} className="tray-inset p-3">
                  <div className="flex justify-between items-baseline gap-3">
                    <span className="text-sm">{f.dimension}</span>
                    <span className="font-mono-data text-xs text-amber-500">{f.score}/{f.max}</span>
                  </div>
                  <div className="text-xs text-text-secondary mt-1">{f.comment}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            {(risk.top_concerns || []).length > 0 && (
              <div>
                <div className="font-mono-data text-[10px] tracking-widest uppercase text-text-secondary mb-2 inline-flex items-center gap-2">
                  <AlertTriangle className="w-3 h-3 text-amber-500" /> Top Concerns
                </div>
                {risk.top_concerns.map((c, i) => (
                  <div key={i} className="border-l-2 border-amber-500/50 ps-3 py-1.5 text-sm">{c}</div>
                ))}
              </div>
            )}
            {(risk.recommended_actions || []).length > 0 && (
              <div>
                <div className="font-mono-data text-[10px] tracking-widest uppercase text-text-secondary mb-2 inline-flex items-center gap-2">
                  <ListChecks className="w-3 h-3 text-emerald-400" /> Recommended Actions
                </div>
                {risk.recommended_actions.map((a, i) => (
                  <div key={i} className="border-l-2 border-emerald-500/50 ps-3 py-1.5 text-sm">{a}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
